import React, { useState, useEffect } from "react";
import { Icon } from "@clarkinc/zest-design-system/ui";
import { User } from "~/common/Store/models/User";
import InstallationServiceUnavailable from "./Pricing/AddToCart/WhatWeOffer/InstallationService/InstallationServiceUnavailable";
import connectUserAndChat from "~/common/Store/connectUserAndChat";
import QuickShip from "./QuickShip";
import { QualityRatingMembership } from "~/common/Store/models/QualityRating";
import GoodBetterBestRibbon from "~/productListing/ProductBox/GoodBetterBestRibbon";
import SpecialOrderInfo from "./SpecialOrderInfo";
import { SourceVendor, NoticesAndWarnings } from "./ProductDetailPageResult";
import getStockCount from "./Pricing/getStockCount";
import StockCount from "./StockCount";
import ShippingMessage from "./ShippingMessage";

interface Props {
  isOutlet: boolean;
  mounted: boolean;
  user: User;
  product: SideSectionProduct;
}

interface SideSectionProduct {
  highlights: string[];
  upc: string;
  isQuickShip: boolean;
  isCommonCarrier: boolean;
  hasDropShip: boolean;
  noticesAndWarnings: NoticesAndWarnings;
  stockMessage: string;
  restrictItemExpeditedShipping: boolean;
  restrictedItemShippingText: string;
  itemNumber: string;
  itemNumberId: number;
  displayInstallationService: boolean;
  isEverliving?: boolean;
  qualityRatingMembership: QualityRatingMembership;
  isConsumable: boolean;
  sourceVendor: SourceVendor;
  serviceTierId: number;
  isInStock: boolean;
  grouping: boolean;
  groupingAttributes: {
    groupMembers: {
      itemNumber: string;
    }[];
  };
}

const ProductDetailHighlightsAndMetaSideSection = connectUserAndChat(
  ({ isOutlet, mounted, user, product }: Props) => {
    const {
      highlights,
      upc,
      isQuickShip,
      hasDropShip,
      noticesAndWarnings,
      stockMessage,
      restrictItemExpeditedShipping,
      restrictedItemShippingText,
      itemNumber,
      itemNumberId,
      isEverliving,
      qualityRatingMembership,
      isConsumable,
      sourceVendor,
    } = product;

    const [stockCount, setStockCount] = useState(0);
    const isSpecialOrder = () => {
      if (
        (noticesAndWarnings?.isSpecialOrder ?? false) &&
        stockMessage === "Special Order"
      ) {
        return true;
      }
      return false;
    };

    const shouldShowGbbRibbon =
      qualityRatingMembership &&
      qualityRatingMembership.groupItemNumbers?.length > 0;

    useEffect(() => {
      const getStockCountByItemNumberId = async (
        itemNumberId: number,
        consumable: boolean,
        vendorCode: string,
      ) => {
        await getStockCount(itemNumberId, consumable, vendorCode).then(
          (stockCount) => {
            setStockCount(!stockCount.isError ? stockCount.value : 0);
          },
        );
      };

      if (user.registered && user.platinum.enabled && itemNumberId > 0) {
        getStockCountByItemNumberId(
          itemNumberId,
          isConsumable,
          sourceVendor.code,
        );
      }
    }, [
      isConsumable,
      itemNumberId,
      sourceVendor.code,
      user.platinum.enabled,
      user.registered,
    ]);

    return (
      <div className="box" data-testid="highlights-meta-side-section">
        <div className="side-section padded">
          <div className="clears" />
          {!isOutlet && (
            <InstallationServiceUnavailable
              user={user}
              mounted={mounted}
              product={product}
            />
          )}
          {(isQuickShip || isOutlet || stockMessage?.length > 0) && (
            <div className="meta" data-component="Meta">
              {isOutlet && (
                <div className="product__stat">
                  <span className="product__stat-title">Condition:</span>
                  <span className="product__stat-desc">Outlet</span>
                </div>
              )}
              <div className="product__ship">
                {(isQuickShip ||
                  stockMessage?.length > 0 ||
                  restrictItemExpeditedShipping) &&
                  !isEverliving && (
                    <>
                      {isQuickShip && (
                        <QuickShip
                          restrictItemExpeditedShipping={
                            restrictItemExpeditedShipping
                          }
                        />
                      )}
                      {isSpecialOrder() && <SpecialOrderInfo />}
                      {!isQuickShip && !isSpecialOrder() && (
                        <ShippingMessage
                          stockMessage={stockMessage}
                          restrictItemExpeditedShipping={
                            restrictItemExpeditedShipping
                          }
                        />
                      )}
                    </>
                  )}
              </div>
            </div>
          )}
          {highlights?.length > 0 && (
            <>
              <h2 className="leading-none text-sm">Product Overview</h2>
              <ul className="highlights">
                {highlights.map((highlight) => (
                  <li className="pl-0" key={highlight}>
                    <div className="flex">
                      <div className="flex">
                        <Icon
                          name="ri-check-fill"
                          size="xsmall"
                          theme="wss"
                          className="relative w-4 h-4 pt-0.5 fill-lime-600"
                        />
                      </div>
                      <div className="ml-2 text-sm">
                        <span dangerouslySetInnerHTML={{ __html: highlight }} />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          {restrictedItemShippingText && (
            <>
              <h4 className="leading-none">Shipping Restrictions</h4>
              <div className="flex">
                <div className="flex">
                  <Icon
                    name="ri-close-fill"
                    size="xsmall"
                    theme="wss"
                    className="relative w-4 h-4 pt-0.5 fill-red-600"
                  />
                </div>
                <div className="ml-2 text-sm">
                  <span>{restrictedItemShippingText}</span>
                </div>
              </div>
            </>
          )}
          <div className="text-sm mt-6">
            {shouldShowGbbRibbon && (
              <div className="product__stat">
                <span className="product__stat-title font-semibold">
                  Quality Rating:
                </span>
                <span className="product__stat-desc relative top-1 left-1.5">
                  <GoodBetterBestRibbon
                    itemNumber={itemNumber}
                    qualityRating={qualityRatingMembership}
                    style={{
                      height: "22px",
                      display: "flex",
                      alignItems: "center",
                      margin: "0",
                      paddingTop: "2px",
                    }}
                    user={user}
                  />
                </span>
              </div>
            )}
            {!isOutlet && upc?.length > 0 && (
              <div className="product__stat">
                <span className="product__stat-title font-semibold">
                  UPC Code:
                </span>
                <span className="product__stat-desc">{upc}</span>
              </div>
            )}
            {!isEverliving && (
              <StockCount
                user={user}
                stockCount={stockCount}
                hasDropShip={hasDropShip}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default ProductDetailHighlightsAndMetaSideSection;
